import * as React from "react";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { OverlayPanel } from "primereact/overlaypanel";

import { distOptions, distParams } from "./DistributionOptions";

const DEFAULT_LEVEL = {
  dist: "uniform",
  min: null,
  max: null,
  arg3: null,
  arg4: null,
};

// Editor for a single factorial level's distribution, opened from
// ExperimentPanel's level column. `ref` is forwarded to the underlying
// OverlayPanel so the caller can toggle it.
const FactorialLevelDistributionOverlay = React.forwardRef(
  (
    { overlayTarget, selectedParameters, numLevels, setParamUpdates, onClose },
    ref,
  ) => {
    const param = overlayTarget
      ? selectedParameters?.find((p) => p.id === overlayTarget.paramId)
      : null;
    const committedLevel = param
      ? param.values?.[overlayTarget.levelIndex]
      : null;
    const committed =
      committedLevel && typeof committedLevel === "object"
        ? committedLevel
        : DEFAULT_LEVEL;

    const [draft, setDraft] = React.useState(committed);

    // Reload the draft whenever a different level's editor is opened, so
    // edits left over from the previous level don't leak into this one.
    React.useEffect(() => {
      setDraft(committed);
    }, [overlayTarget?.paramId, overlayTarget?.levelIndex]);

    const isChanged =
      draft.dist !== committed.dist ||
      draft.min !== committed.min ||
      draft.max !== committed.max ||
      draft.arg3 !== committed.arg3 ||
      draft.arg4 !== committed.arg4;

    const handleFieldChange = (field, value) => {
      setDraft((prev) => ({ ...prev, [field]: value }));
    };

    const applyDraft = () => {
      if (!param || !overlayTarget) return;
      setParamUpdates((prev) => {
        const values = prev[param.id]?.values
          ? [...prev[param.id].values]
          : Array(numLevels).fill(null);
        values[overlayTarget.levelIndex] = { ...draft };
        return { ...prev, [param.id]: { ...prev[param.id], values } };
      });
    };

    const handleHide = () => {
      applyDraft();
      onClose?.();
    };

    const dist = draft.dist;

    return (
      <OverlayPanel ref={ref} showCloseIcon dismissable onHide={handleHide}>
        {param && overlayTarget && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
              minWidth: "220px",
            }}
          >
            <label>Distribution</label>
            <Dropdown
              value={dist}
              options={distOptions}
              onChange={(event) => handleFieldChange("dist", event.value)}
            />

            <label>Min</label>
            <InputNumber
              value={draft.min ?? null}
              onValueChange={(event) => handleFieldChange("min", event.value)}
              disabled={!distParams[dist].min}
              inputStyle={{ width: "100%" }}
            />

            <label>Max</label>
            <InputNumber
              value={draft.max ?? null}
              onValueChange={(event) => handleFieldChange("max", event.value)}
              disabled={!distParams[dist].max}
              inputStyle={{ width: "100%" }}
            />

            <label>{distParams[dist].arg3 || "Arg 3"}</label>
            <InputNumber
              value={draft.arg3 ?? null}
              onValueChange={(event) => handleFieldChange("arg3", event.value)}
              disabled={!distParams[dist].arg3}
              maxFractionDigits={2}
              inputStyle={{ width: "100%" }}
            />

            <label>{distParams[dist].arg4 || "Arg 4"}</label>
            <InputNumber
              value={draft.arg4 ?? null}
              onValueChange={(event) => handleFieldChange("arg4", event.value)}
              disabled={!distParams[dist].arg4}
              maxFractionDigits={2}
              inputStyle={{ width: "100%" }}
            />

            {isChanged && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  fontSize: "0.8rem",
                  color: "#b45309",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: "#b45309",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                Unapplied changes
              </div>
            )}

            <Button
              label="Apply"
              disabled={!isChanged}
              onClick={() => {
                applyDraft();
                ref.current?.hide();
              }}
            />
          </div>
        )}
      </OverlayPanel>
    );
  },
);

export default FactorialLevelDistributionOverlay;
